/**
 * StepCard Component
 * Displays a single step in a schedule with drag handle, action image, title, and timer badge.
 */
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Timer as TimerIcon,
  CheckCircle as CheckmarkIcon,
  TouchApp as TapIcon,
  SwipeRight as SwipeIcon,
  PanTool as HoldIcon,
  PlayCircle as AutoIcon,
  Lock as ParentOnlyIcon,
} from '@mui/icons-material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { ScheduleStep, CompletionAction, formatTimerDisplay } from '../../types/schedule';

interface StepCardProps {
  step: ScheduleStep;
  onClick?: () => void;
  onDelete?: () => void;
  actionImage?: string | null;
  actionName?: string;
  scheduleName?: string;  // For schedule-type steps
  scheduleIcon?: string;  // For schedule-type steps
  isDragging?: boolean;
}

const COMPLETION_ICONS: Record<CompletionAction, React.ReactNode> = {
  checkmark: <CheckmarkIcon fontSize="small" />,
  tap: <TapIcon fontSize="small" />,
  swipe: <SwipeIcon fontSize="small" />,
  hold: <HoldIcon fontSize="small" />,
  auto: <AutoIcon fontSize="small" />,
  parent_only: <ParentOnlyIcon fontSize="small" />,
};

export const StepCard: React.FC<StepCardProps> = ({
  step,
  onClick,
  onDelete,
  actionImage,
  actionName,
  scheduleName,
  scheduleIcon,
  isDragging = false,
}) => {
  const { t } = useTranslation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: sortableIsDragging,
  } = useSortable({ id: step.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: sortableIsDragging ? 0.5 : 1,
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.();
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { boxShadow: 3 } : {},
        bgcolor: step.isRewardStep ? 'success.50' : 'background.paper',
      }}
      onClick={onClick}
    >
      {/* Drag handle */}
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
          cursor: 'grab',
          color: 'text.secondary',
          '&:hover': { color: 'text.primary' },
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIcon />
      </Box>

      {/* Order number */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '0.875rem',
          flexShrink: 0,
        }}
      >
        {step.order + 1}
      </Box>

      {/* Action image or icon */}
      {step.stepType === 'action' && actionImage ? (
        <Box
          component="img"
          src={actionImage}
          alt={step.title}
          sx={{
            width: 48,
            height: 48,
            objectFit: 'cover',
            borderRadius: 1,
            ml: 1.5,
            flexShrink: 0,
          }}
        />
      ) : step.stepType === 'schedule' ? (
        <Box
          sx={{
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'info.50',
            border: '2px solid',
            borderColor: 'info.main',
            borderRadius: 1,
            ml: 1.5,
            fontSize: '1.5rem',
            flexShrink: 0,
          }}
        >
          {scheduleIcon || '📅'}
        </Box>
      ) : (
        <Box
          sx={{
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.200',
            borderRadius: 1,
            ml: 1.5,
            fontSize: '1.5rem',
            flexShrink: 0,
          }}
        >
          📋
        </Box>
      )}

      {/* Content */}
      <CardContent sx={{ flexGrow: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 500 }}>
          {step.title}
        </Typography>
        {step.subtitle && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {step.subtitle}
          </Typography>
        )}
        {step.stepType === 'action' && actionName && actionName !== step.title && (
          <Typography variant="caption" color="text.secondary">
            From: {actionName}
          </Typography>
        )}
        {step.stepType === 'schedule' && scheduleName && (
          <Chip
            size="small"
            label={scheduleName}
            color="info"
            variant="outlined"
            sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
          />
        )}
      </CardContent>

      {/* Badges */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1 }}>
        {/* Timer badge */}
        {step.timer && (
          <Tooltip title={t('schedules.steps.timer')}>
            <Chip
              icon={<TimerIcon />}
              label={formatTimerDisplay(step.timer.durationSeconds)}
              size="small"
              variant="outlined"
              sx={{ height: 24 }}
            />
          </Tooltip>
        )}

        {/* Completion action icon */}
        <Tooltip title={t(`schedules.completionActions.${step.completionAction}`)}>
          <Box
            sx={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            {COMPLETION_ICONS[step.completionAction]}
          </Box>
        </Tooltip>

        {/* Token value */}
        {step.tokenValue > 0 && (
          <Chip
            label={`${step.tokenValue} 🪙`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ height: 24 }}
          />
        )}
      </Box>

      {/* Delete button */}
      {onDelete && (
        <IconButton
          size="small"
          onClick={handleDeleteClick}
          sx={{ mr: 0.5, color: 'text.secondary' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Card>
  );
};
